import type { ReadonlySignal } from "@preact/signals";
import type { FormStore } from "@formisch/preact";
import { setInput } from "@formisch/preact";

import { replaceLegalLinks } from "../theme.js";
import { InputError } from "./fields/input-error.js";
import type { FieldProps } from "./fields/shared.js";

interface ConsentCheckboxProps {
  text: string;
  required: boolean;
  input: ReadonlySignal<any>;
  errors: ReadonlySignal<[string, ...string[]] | null>;
  props: FieldProps;
  disabled: boolean;
  form: FormStore;
}

function sanitizeHtml(html: string): string {
  const stripped = html
    .replace(/<(?!\/?(?:a|br)\b)[^>]*>/gi, "")
    .replace(/<br\s[^>]*>/gi, "<br>");

  return stripped.replace(/<a\s+([^>]*)>/gi, (_match, attrs: string) => {
    const hrefMatch = attrs.match(/href\s*=\s*["']([^"']*)["']/i);
    if (!hrefMatch || !/^https?:\/\//i.test(hrefMatch[1])) return "<a>";

    const safeAttrs = [`href="${hrefMatch[1]}"`, 'target="_blank"', 'rel="noopener noreferrer"'];
    return `<a ${safeAttrs.join(" ")}>`;
  });
}

export function ConsentCheckbox({ text, required, input, errors, props, disabled, form }: ConsentCheckboxProps) {
  const html = sanitizeHtml(replaceLegalLinks(text));

  return (
    <div class="flex flex-col gap-1 w-full" data-fv-field="_consent">
      <div class="flex items-start gap-2.5 mt-1">
        {required && (
          <input
            {...props}
            id={props.name}
            type="checkbox"
            class="w-4 h-4 mt-0.5 shrink-0 cursor-pointer"
            style={{ accentColor: "var(--fv-theme-primary, var(--fv-button-bg, #007145))" }}
            checked={input}
            disabled={disabled}
            onChange={(e: Event) => setInput(form, { path: ["_consent"] as any, input: (e.target as HTMLInputElement).checked })}
            aria-invalid={!!errors.value}
            aria-errormessage={`${props.name}-error`}
          />
        )}
        <label
          class="text-xs leading-relaxed text-theme-muted-fg cursor-pointer [&_a]:text-theme-fg [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-theme-primary"
          for={required ? props.name : undefined}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
      <InputError name={props.name} errors={errors} />
    </div>
  );
}
